import { useState, useEffect } from "react";
import { RateAnalysis } from "../types/rate-analysis";
import { LOCAL_STORAGE_KEYS } from "../lib/constants";
import { useLocation } from "react-router-dom";

interface QuoteDetails {
  rate: string;
  lenderFees: string;
  titleFees: string;
  mortgageInsurance: string;
}

interface LoanInfo {
  loanType: "refinance" | "purchase" | "";
  isFirstHome: boolean | null;
  propertyType: "primary" | "second" | "investment" | "";
  propertyStyle:
    | "single"
    | "townhouse"
    | "duplex"
    | "condo"
    | "manufactured"
    | "";
  militaryService: boolean | null;
  zipCode: string;
  purchasePrice: string;
  downPayment: string;
  downPaymentPercent: string;
  creditScore: string;
  propertyValue: string;
  remainingBalance: string;
  cashOut: boolean | null;
  cashOutAmount: string;
}

export const useQuoteRequestState = () => {
  const location = useLocation();
  const [requestId, setRequestId] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<RateAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quoteDetails, setQuoteDetails] = useState<QuoteDetails>({
    rate: "",
    lenderFees: "",
    titleFees: "",
    mortgageInsurance: "",
  });
  const [questions, setQuestions] = useState<string[]>([""]);
  const [loanInfo, setLoanInfo] = useState<LoanInfo>({
    loanType: "",
    isFirstHome: null,
    propertyType: "",
    propertyStyle: "",
    militaryService: null,
    zipCode: "",
    purchasePrice: "",
    downPayment: "",
    downPaymentPercent: "",
    creditScore: "",
    propertyValue: "",
    remainingBalance: "",
    cashOut: null,
    cashOutAmount: "",
  });

  useEffect(() => {
    // Check for qr query parameter first
    const searchParams = new URLSearchParams(location.search);
    const qrParam = searchParams.get("qr");

    // Load all data from localStorage
    const storedId = localStorage.getItem(LOCAL_STORAGE_KEYS.QUOTE_REQUEST_ID);
    const storedDetails = localStorage.getItem(
      LOCAL_STORAGE_KEYS.QUOTE_DETAILS
    );
    const storedQuestions = localStorage.getItem(
      LOCAL_STORAGE_KEYS.QUOTE_QUESTIONS
    );
    const storedLoanInfo = localStorage.getItem(LOCAL_STORAGE_KEYS.LOAN_INFO);

    // Use qr param if it exists, otherwise use stored ID
    if (qrParam) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.QUOTE_REQUEST_ID, qrParam);
      setRequestId(qrParam);
    } else if (storedId) {
      setRequestId(storedId);
    }

    if (storedDetails) {
      setQuoteDetails(JSON.parse(storedDetails));
    }
    if (storedQuestions) {
      setQuestions(JSON.parse(storedQuestions));
    }
    if (storedLoanInfo) {
      setLoanInfo(JSON.parse(storedLoanInfo));
    }
    setIsLoading(false);
  }, [location.search]);

  const saveRequestId = (id: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.QUOTE_REQUEST_ID, id);
    setRequestId(id);
  };

  const saveQuoteDetails = (details: QuoteDetails) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.QUOTE_DETAILS,
      JSON.stringify(details)
    );
    setQuoteDetails(details);
  };

  const saveQuestions = (newQuestions: string[]) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.QUOTE_QUESTIONS,
      JSON.stringify(newQuestions)
    );
    setQuestions(newQuestions);
  };

  const saveLoanInfo = (info: LoanInfo) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.LOAN_INFO, JSON.stringify(info));
    setLoanInfo(info);
  };

  const clearRequestId = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.QUOTE_REQUEST_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.QUOTE_DETAILS);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.QUOTE_QUESTIONS);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LOAN_INFO);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.UPLOADED_QUOTE);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFI_BUDDY_DATA);
    setRequestId(null);
    setAnalysis(null);
    setQuoteDetails({
      rate: "",
      lenderFees: "",
      titleFees: "",
      mortgageInsurance: "",
    });
    setQuestions([""]);
    setLoanInfo({
      loanType: "",
      isFirstHome: null,
      propertyType: "",
      propertyStyle: "",
      militaryService: null,
      zipCode: "",
      purchasePrice: "",
      downPayment: "",
      downPaymentPercent: "",
      creditScore: "",
      propertyValue: "",
      remainingBalance: "",
      cashOut: null,
      cashOutAmount: "",
    });
  };

  return {
    requestId,
    analysis,
    setAnalysis,
    saveRequestId,
    clearRequestId,
    isLoading,
    quoteDetails,
    setQuoteDetails: saveQuoteDetails,
    questions,
    setQuestions: saveQuestions,
    loanInfo,
    setLoanInfo: saveLoanInfo,
  };
};
